/**
 * Unified FHIR Vital Signs Service
 * Central service for all vital signs, on one FHIR-based data structure
 * Ersetzt alle bisherigen Vitaldaten-Services
 */

import { api } from "./api";
import {
  VitalSignsData,
  VitalSignsInput,
  VitalSignsResponse,
  VitalSignsHistoryItem,
  VitalSignsValidation,
  VitalSignsStatus,
  VitalSignsDisplayData,
  VitalSignsChartData,
  FHIRVitalObservation,
  VITAL_SIGNS_LIMITS,
  FHIR_LOINC_CODES,
} from "@/types/fhir-vitals";
import { useI18nStore } from "@/stores/i18nStore";
import { formatChartDate, formatFullTimestamp } from "@/utils/dateLocalization";

/**
 * What GET /api/vital-signs returns (backend vital-signs.routes.ts). Every
 * measurement is initialised to null there and only filled when a matching
 * LOINC Observation exists, so absence is normal - which is precisely what the
 * "never use fallback values" note in this file is protecting.
 *
 * `fhirId` is read by the transform but is not sent by that endpoint today; it
 * stays optional rather than being invented.
 */
interface BackendVitalSigns {
  systolicBloodPressure?: number | null;
  diastolicBloodPressure?: number | null;
  restingHeartRate?: number | null;
  recordedAt?: string | null;
  fhirId?: string | null;
}

export class UnifiedVitalSignsService {
  /**
   * Save new vital signs as FHIR observations
   */
  async saveVitalSigns(data: VitalSignsInput): Promise<VitalSignsResponse> {
    try {
      const response = await api.post("/vital-signs", data);
      return response.data;
    } catch (error: any) {
      console.error("Error saving vital signs:", error);
      throw error;
    }
  }

  /**
   * Load the latest vital signs from the FHIR store
   */
  async getLatestVitalSigns(): Promise<VitalSignsData | null> {
    try {
      const response = await api.get("/vital-signs/latest");

      if (response.data?.success && response.data.data?.latestVitalSigns) {
        const vitals = response.data.data.latestVitalSigns;
        return this.transformToUnifiedFormat(vitals);
      }

      return null;
    } catch (error: any) {
      console.error("Error fetching latest vital signs:", error);
      return null;
    }
  }

  /**
   * Load the vital signs history for charts (365 days) - UPDATED v1.2.0
   */
  async getVitalSignsHistory(
    days: number = 365,
  ): Promise<VitalSignsHistoryItem[]> {
    try {
      const response = await api.get("/vital-signs/history", {
        params: { days },
      });

      if (response.data?.success && response.data.data?.history) {
        return response.data.data.history;
      }

      return [];
    } catch (error: any) {
      console.error("Error fetching vital signs history:", error);
      return [];
    }
  }

  /**
   * Load vital signs for the dashboard
   */
  async getVitalSignsForDashboard(): Promise<VitalSignsDisplayData | null> {
    try {
      const latestVitalSigns = await this.getLatestVitalSigns();

      if (!latestVitalSigns) {
        return null;
      }

      const {
        systolicBloodPressure,
        diastolicBloodPressure,
        restingHeartRate,
      } = latestVitalSigns;

      // A partial reading has no dashboard summary: calculateVitalSignsStatus
      // would classify a blood pressure from missing numbers, which is exactly
      // the synthetic-value behaviour this service exists to avoid. Absent data
      // reads as absent.
      if (
        systolicBloodPressure === undefined ||
        diastolicBloodPressure === undefined ||
        restingHeartRate === undefined
      ) {
        return null;
      }

      return {
        systolic: systolicBloodPressure,
        diastolic: diastolicBloodPressure,
        heartRate: restingHeartRate,
        recordedAt: latestVitalSigns.recordedAt,
        status: this.calculateVitalSignsStatus(
          systolicBloodPressure,
          diastolicBloodPressure,
          restingHeartRate,
        ),
      };
    } catch (error: any) {
      console.error("Error fetching vital signs for dashboard:", error);
      return null;
    }
  }

  /**
   * Load vital signs for the Health Records page
   */
  async getVitalSignsForHealthRecords(): Promise<VitalSignsData | null> {
    // Nutzt dieselbe Methode, da einheitliche Datenstruktur
    return await this.getLatestVitalSigns();
  }

  /**
   * Load chart data for the trend view (365 days) - UPDATED v1.2.0
   */
  async getVitalSignsChartData(
    days: number = 365,
  ): Promise<VitalSignsChartData[]> {
    try {
      const history = await this.getVitalSignsHistory(days);

      return history.map((item) => ({
        date: item.date,
        systolic: item.systolic,
        diastolic: item.diastolic,
        heartRate: item.heartRate,
        timestamp: item.timestamp,
        formattedDate: this.formatDateForChart(item.date),
      }));
    } catch (error: any) {
      console.error("Error preparing chart data:", error);
      return [];
    }
  }

  /**
   * Validate vital signs input; messages in the app language
   */
  validateVitalSigns(data: Partial<VitalSignsInput>): VitalSignsValidation {
    const errors: string[] = [];
    const fieldErrors: Record<string, string> = {};
    const { t } = useI18nStore.getState();

    // Systolic blood pressure
    if (
      !data.systolicBloodPressure ||
      data.systolicBloodPressure < VITAL_SIGNS_LIMITS.SYSTOLIC.min ||
      data.systolicBloodPressure > VITAL_SIGNS_LIMITS.SYSTOLIC.max
    ) {
      const error = t.systolicBpError;
      errors.push(error);
      fieldErrors.systolic = error;
    }

    // Diastolic blood pressure
    if (
      !data.diastolicBloodPressure ||
      data.diastolicBloodPressure < VITAL_SIGNS_LIMITS.DIASTOLIC.min ||
      data.diastolicBloodPressure > VITAL_SIGNS_LIMITS.DIASTOLIC.max
    ) {
      const error = t.diastolicBpError;
      errors.push(error);
      fieldErrors.diastolic = error;
    }

    // Heart rate
    if (
      !data.restingHeartRate ||
      data.restingHeartRate < VITAL_SIGNS_LIMITS.HEART_RATE.min ||
      data.restingHeartRate > VITAL_SIGNS_LIMITS.HEART_RATE.max
    ) {
      const error = t.heartRateError;
      errors.push(error);
      fieldErrors.heartRate = error;
    }

    // Systolic must exceed diastolic
    if (
      data.systolicBloodPressure &&
      data.diastolicBloodPressure &&
      data.systolicBloodPressure <= data.diastolicBloodPressure
    ) {
      const error = t.systolicMustBeHigher;
      errors.push(error);
      fieldErrors.pressure = error;
    }

    return {
      isValid: errors.length === 0,
      errors,
      fieldErrors,
    };
  }

  /**
   * Classify vital signs for display
   */
  calculateVitalSignsStatus(
    systolic: number,
    diastolic: number,
    heartRate: number,
  ): VitalSignsStatus {
    // Blood pressure category
    let bpCategory: VitalSignsStatus["bloodPressure"]["category"];
    let bpLabel: string;
    let bpColor: string;
    let bpBgColor: string;

    if (systolic < 90 || diastolic < 60) {
      bpCategory = "low";
      bpLabel = "lowBloodPressure"; // i18n key
      bpColor = "text-blue-600";
      bpBgColor = "bg-blue-50";
    } else if (systolic <= 120 && diastolic <= 80) {
      bpCategory = "normal";
      bpLabel = "normal"; // i18n key
      bpColor = "text-green-600";
      bpBgColor = "bg-green-50";
    } else if (systolic <= 129 && diastolic <= 80) {
      bpCategory = "elevated";
      bpLabel = "elevated"; // i18n key
      bpColor = "text-yellow-600";
      bpBgColor = "bg-yellow-50";
    } else if (systolic <= 139 || diastolic <= 89) {
      bpCategory = "high_stage1";
      bpLabel = "hypertensionGrade1"; // i18n key
      bpColor = "text-orange-600";
      bpBgColor = "bg-orange-50";
    } else if (systolic <= 179 || diastolic <= 109) {
      bpCategory = "high_stage2";
      bpLabel = "hypertensionGrade2"; // i18n key
      bpColor = "text-red-600";
      bpBgColor = "bg-red-50";
    } else {
      bpCategory = "hypertensive_crisis";
      bpLabel = "hypertensionGrade3"; // i18n key
      bpColor = "text-red-800";
      bpBgColor = "bg-red-100";
    }

    // Heart rate category
    let hrCategory: VitalSignsStatus["heartRate"]["category"];
    let hrLabel: string;
    let hrColor: string;
    let hrBgColor: string;

    if (heartRate < 60) {
      hrCategory = "bradycardia";
      hrLabel = "bradycardia"; // i18n key
      hrColor = "text-blue-600";
      hrBgColor = "bg-blue-50";
    } else if (heartRate <= 100) {
      hrCategory = "normal";
      hrLabel = "normal"; // i18n key
      hrColor = "text-green-600";
      hrBgColor = "bg-green-50";
    } else {
      hrCategory = "tachycardia";
      hrLabel = "tachycardia"; // i18n key
      hrColor = "text-red-600";
      hrBgColor = "bg-red-50";
    }

    return {
      bloodPressure: {
        category: bpCategory,
        label: bpLabel,
        color: bpColor,
        bgColor: bpBgColor,
      },
      heartRate: {
        category: hrCategory,
        label: hrLabel,
        color: hrColor,
        bgColor: hrBgColor,
      },
    };
  }

  /**
   * Convert backend data to the unified format
   */
  private transformToUnifiedFormat(
    backendData: BackendVitalSigns,
  ): VitalSignsData {
    // ✅ REGRESSION TEST PROTECTION: NEVER use fallback values
    // Real FHIR data should always be returned, not synthetic defaults

    const systolic = backendData.systolicBloodPressure ?? undefined;
    const diastolic = backendData.diastolicBloodPressure ?? undefined;

    const pulsPressure =
      systolic && diastolic ? systolic - diastolic : undefined;

    const meanArterialPressure =
      diastolic && pulsPressure
        ? Math.round(diastolic + pulsPressure / 3)
        : undefined;

    return {
      // ✅ CRITICAL: Use actual FHIR data, no fallbacks that mask missing data
      systolicBloodPressure: systolic,
      diastolicBloodPressure: diastolic,
      bloodPressureUnit: "mmHg",
      restingHeartRate: backendData.restingHeartRate ?? undefined,
      heartRateUnit: "bpm",
      recordedAt: backendData.recordedAt || new Date().toISOString(),
      fhirId: backendData.fhirId ?? undefined,
      fhirStatus: "final",
      pulsPressure,
      meanArterialPressure,
    };
  }

  /**
   * Format a date for chart display, in the app language
   * @deprecated Use formatChartDate from utils/dateLocalization.ts instead
   */
  private formatDateForChart(dateString: string): string {
    return formatChartDate(dateString, useI18nStore.getState().language);
  }

  /**
   * Format a timestamp for display, in the app language
   * @deprecated Use formatFullTimestamp from utils/dateLocalization.ts instead
   */
  formatRecordedAt(timestamp: string): string {
    return formatFullTimestamp(timestamp, useI18nStore.getState().language);
  }

  /**
   * Build a FHIR Observation from UI data
   */
  createFHIRObservation(
    data: VitalSignsInput,
    userId?: string,
  ): FHIRVitalObservation {
    const timestamp = new Date().toISOString();

    return {
      resourceType: "Observation",
      id: `vital-signs-${Date.now()}`,
      status: "final",
      category: [
        {
          coding: [
            {
              system:
                "http://terminology.hl7.org/CodeSystem/observation-category",
              code: FHIR_LOINC_CODES.VITAL_SIGNS_CATEGORY,
              display: "Vital Signs",
            },
          ],
        },
      ],
      code: {
        coding: [
          {
            system: "http://loinc.org",
            code: FHIR_LOINC_CODES.BLOOD_PRESSURE_PANEL,
            display: "Blood pressure panel with all children optional",
          },
        ],
      },
      subject: {
        reference: userId ? `Patient/${userId}` : "Patient/current-user",
        display: "Patient",
      },
      effectiveDateTime: timestamp,
      issued: timestamp,
      component: [
        {
          code: {
            coding: [
              {
                system: "http://loinc.org",
                code: FHIR_LOINC_CODES.SYSTOLIC_BP,
                display: "Systolic blood pressure",
              },
            ],
          },
          valueQuantity: {
            value: data.systolicBloodPressure || 120,
            unit: "mmHg",
            system: "http://unitsofmeasure.org",
            code: "mm[Hg]",
          },
        },
        {
          code: {
            coding: [
              {
                system: "http://loinc.org",
                code: FHIR_LOINC_CODES.DIASTOLIC_BP,
                display: "Diastolic blood pressure",
              },
            ],
          },
          valueQuantity: {
            value: data.diastolicBloodPressure || 80,
            unit: "mmHg",
            system: "http://unitsofmeasure.org",
            code: "mm[Hg]",
          },
        },
      ],
      meta: {
        lastUpdated: timestamp,
        versionId: "1",
      },
    };
  }
}

// Singleton Instance Export
export const unifiedVitalSignsService = new UnifiedVitalSignsService();
